/**
 * useNoteAuditDetail Hook
 *
 * Fetches and manages single note audit detail
 */

import { useMemo } from 'react';

import { useGetNoteAuditDetailQuery } from '../store/api';
import type { AuditData,NoteAuditDetail } from '../types';

interface UseNoteAuditDetailResult {
  /** Note audit detail */
  audit: NoteAuditDetail | undefined;
  /** Parsed audit data (from current_revision.audit_data) */
  auditData: AuditData | null;
  /** Whether data is loading */
  isLoading: boolean;
  /** Whether a fetch is currently in flight (includes refetch) */
  isFetching: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
}

export const useNoteAuditDetail = (noteId: string | undefined): UseNoteAuditDetailResult => {
  const { data, isLoading, isFetching, error, refetch } = useGetNoteAuditDetailQuery(
    { noteId: noteId! },
    { skip: !noteId },
  );

  // Parse the audit_data JSONB
  const auditData = useMemo((): AuditData | null => {
    const rawData = data?.current_revision?.audit_data;
    if (!rawData) return null;

    try {
      // The data might already be parsed or might be a string
      const parsed = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      return {
        flags: parsed.flags || [],
        questions: parsed.questions || [],
        rule_compliance_assessments: parsed.rule_compliance_assessments || [],
        billing_support: parsed.billing_support || {
          supports_suggested_cpt: false,
          proposed_primary_code: null,
          confidence: 0,
          strengths: [],
          concerns: [],
          missing_elements_for_code: [],
        },
        scores: parsed.scores || {
          completeness: 0,
          specificity: 0,
          compliance: 0,
          clinical_quality: 0,
        },
        flags_summary: parsed.flags_summary || '',
      };
    } catch {
      return null;
    }
  }, [data]);

  return {
    audit: data,
    auditData,
    isLoading,
    isFetching,
    error: error ? 'Failed to load note audit details' : null,
    refetch,
  };
};

export default useNoteAuditDetail;
